/**
 * Shopify Bundle Adapter - Theme Integration Version
 * Connects bundle system to real Shopify product data
 */

class BundleShopifyAdapter {
  constructor() {
    this.productCache = new Map();
    this.eligibleAddonsCache = new Map(); // Cache eligible addons per product
    this.config = null;
    this.configSource = null;
    this.eligibleAddons = null; // Store most recent eligible addons for easy access
  }

  async initialize(config) {
    this.config = config;
  }

  /**
   * Load configuration dynamically from API with fallback to local JSON
   * @returns {Promise<Object>} Bundle configuration object
   */
  async loadConfiguration() {
    // Try API first (app proxy)
    try {
      const shop = window.Shopify?.shop;
      if (shop) {
        const apiUrl = `/apps/bundle-discount/config?shop=${shop}`;

        const response = await fetch(apiUrl);

        if (response.ok) {
          this.config = await response.json();
          this.configSource = 'api';
          return this.config;
        } else {
          console.warn('⚠️  API returned status:', response.status, '- falling back to local config');
        }
      }
    } catch (error) {
      console.warn('⚠️  API fetch failed:', error.message, '- falling back to local config');
    }

    // Fallback to local JSON file
    return this.loadLocalConfig();
  }

  /**
   * Load configuration from local bundle-config.json file
   * @returns {Promise<Object>} Bundle configuration object
   */
  async loadLocalConfig() {
    try {
      const response = await fetch('/assets/bundle-config.json');
      if (!response.ok) {
        throw new Error(`Failed to load bundle-config.json: ${response.status}`);
      }
      this.config = await response.json();
      this.configSource = 'local';
      return this.config;
    } catch (error) {
      console.error('❌ Failed to load local configuration:', error);
      throw error;
    }
  }

  /**
   * Manually refresh configuration (useful for testing)
   * @returns {Promise<Object>} Refreshed configuration object
   */
  async refreshConfiguration() {
    this.config = null;
    this.configSource = null;
    return this.loadConfiguration();
  }

  /**
   * Get the current configuration source
   * @returns {string} 'api', 'local', or 'unknown'
   */
  getConfigSource() {
    return this.configSource || 'unknown';
  }

  async getProduct(handle) {
    if (this.productCache.has(handle)) {
      return this.productCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        console.warn(`[BundleAdapter] Product not found: ${handle}`);
        return null;
      }

      const product = await response.json();
      this.productCache.set(handle, product);
      return product;
    } catch (error) {
      console.error(`[BundleAdapter] Error fetching product ${handle}:`, error);
      return null;
    }
  }

  getCurrentProduct() {
    // Get from window context set by Liquid
    if (window.bundleCurrentProduct) {
      return window.bundleCurrentProduct;
    }
    return null;
  }

  isAddonEligible(addon, product, skipAgeFilter = false) {
    if (!product || !product.tags) return false;

    // Specific product handle match
    if (addon.specificProducts && addon.specificProducts.length > 0) {
      return addon.specificProducts.includes(product.handle);
    }

    // Check exclude tags
    if (addon.excludeTags && addon.excludeTags.length > 0) {
      const hasExcludedTag = addon.excludeTags.some(tag => product.tags.includes(tag));
      if (hasExcludedTag) return false;
    }

    // Check eligibility tags
    if (addon.eligibilityTags && addon.eligibilityTags.length > 0) {
      const hasEligibleTag = addon.eligibilityTags.some(tag => product.tags.includes(tag));
      if (!hasEligibleTag) return false;
    }

    // Check age filter for helmets (kids vs adult)
    // Skip age filter for custom configurator (user makes conscious choice)
    if (addon.ageFilter && !skipAgeFilter) {
      const ageData = this.getProductAgeData(product);
      return this.checkAgeFilter(addon.ageFilter, ageData);
    }

    return true;
  }

  /**
   * Get age data from the age widget or product tags
   * @param {Object} product - Current product
   * @returns {Object} Age data object
   */
  getProductAgeData(product) {
    const ageData = {
      taggedAgeNumeric: null,
      selectedAge: null,
      physicalFitAge: null,
      finalRecommendedAge: null,
      isRegulatoryVehicle: false, // 14+ required
      powerLevel: null,
      productType: 'electric', // 'electric' or 'petrol'
      engineCC: 0 // Engine size in CC for petrol vehicles
    };

    // Try to get data from the age widget first
    const ageWidget = window.productWidgetsOrchestrator?.getWidget('age-fit-unified');
    if (ageWidget) {
      ageData.taggedAgeNumeric = ageWidget.taggedAgeNumeric;
      ageData.selectedAge = ageWidget.selectedAge;
      ageData.physicalFitAge = ageWidget.physicalFitAge;
      ageData.finalRecommendedAge = ageWidget.finalRecommendedAge;
      ageData.isRegulatoryVehicle = ageWidget.isRegulatoryVehicle || false;
      ageData.powerLevel = ageWidget.powerLevel;
      ageData.productType = ageWidget.productType || 'electric';
      ageData.engineCC = ageWidget.engineCC || 0;
    }

    // Fallback: Extract age from product tags if widget not available
    if (!ageData.taggedAgeNumeric && product?.tags) {
      const maxAgeTag = product.tags.find(tag => tag.startsWith('Max User Age_'));
      if (maxAgeTag) {
        const ageValue = maxAgeTag.replace('Max User Age_', '');
        // Parse different formats: "Up to 14 Years Old", "14+", "3-8 Years"
        const plusMatch = ageValue.match(/(\d+)\+/);
        const upToMatch = ageValue.match(/[Uu]p to (\d+)/);
        const rangeMatch = ageValue.match(/(\d+)\s*-\s*(\d+)/);

        if (plusMatch) {
          ageData.taggedAgeNumeric = parseInt(plusMatch[1]);
          ageData.isRegulatoryVehicle = ageData.taggedAgeNumeric >= 14;
        } else if (upToMatch) {
          ageData.taggedAgeNumeric = parseInt(upToMatch[1]);
        } else if (rangeMatch) {
          // Use max of range
          ageData.taggedAgeNumeric = parseInt(rangeMatch[2]);
        }
      }
    }

    return ageData;
  }

  /**
   * Check if addon passes age filter
   * @param {string} ageFilter - 'kids' or 'adult'
   * @param {Object} ageData - Age data from getProductAgeData
   * @returns {boolean}
   */
  checkAgeFilter(ageFilter, ageData) {
    // Age threshold: under 14 = kids, 14+ = adult
    const ADULT_AGE_THRESHOLD = 14;

    // Priority 1: Petrol vehicles with 150cc+ are ALWAYS adult products
    if (ageData.productType === 'petrol' && ageData.engineCC >= 150) {
      return ageFilter === 'adult';
    }

    // Priority 2: User-selected age from calculator (most explicit intent)
    if (ageData.selectedAge !== null) {
      const isAdult = ageData.selectedAge >= ADULT_AGE_THRESHOLD;
      return ageFilter === 'adult' ? isAdult : !isAdult;
    }

    // Priority 3: Regulatory vehicle (14+ required) always adult
    if (ageData.isRegulatoryVehicle) {
      return ageFilter === 'adult';
    }

    // Priority 4: Electric high/extreme power = adult
    if (ageData.productType === 'electric' && (ageData.powerLevel === 'extreme' || ageData.powerLevel === 'high')) {
      return ageFilter === 'adult';
    }

    // Priority 5: Tagged age from product
    if (ageData.taggedAgeNumeric !== null) {
      // For "Up to X" ages, if X < 14, it's a kids product
      // For "14+" style tags, show adult helmet
      const isAdultProduct = ageData.taggedAgeNumeric >= ADULT_AGE_THRESHOLD;
      return ageFilter === 'adult' ? isAdultProduct : !isAdultProduct;
    }

    // Default: Show kids items for products without clear age data
    // (most ride-ons are for children)
    return ageFilter === 'kids';
  }

  async loadEligibleAddons(currentProduct, skipAgeFilter = false) {
    if (!this.config) {
      throw new Error('Adapter not initialized');
    }

    // Check cache first (keyed by product ID + age filter context)
    const cacheKey = `${currentProduct?.id || 'unknown'}_${skipAgeFilter ? 'noAgeFilter' : 'withAgeFilter'}`;
    if (this.eligibleAddonsCache.has(cacheKey)) {
      return this.eligibleAddonsCache.get(cacheKey);
    }

    const eligibleAddons = {
      performance: [],
      personalization: [],
      protection: [],
      gifting: []
    };

    for (const [category, addons] of Object.entries(this.config.addons)) {
      const categoryAddons = await Promise.all(
        addons
          .filter(addon => addon.enabled !== false && this.isAddonEligible(addon, currentProduct, skipAgeFilter))
          .map(async (addon) => {
            const product = await this.getProduct(addon.productHandle);
            if (!product || !product.available) {
              return null;
            }

            // CRITICAL: Check if at least ONE variant is actually in stock
            const hasAvailableVariant = product.variants.some(v => v.available);
            if (!hasAvailableVariant) {
              console.log(`[BundleAdapter] Excluding ${product.handle} - no variants in stock`);
              return null;
            }

            return this.mergeAddonData(addon, product);
          })
      );
      eligibleAddons[category] = categoryAddons.filter(Boolean);
    }

    // Cache the result
    this.eligibleAddonsCache.set(cacheKey, eligibleAddons);

    // Store for easy access by other components (like help modals)
    // Only store the age-filtered version as the default
    if (!skipAgeFilter) {
      this.eligibleAddons = eligibleAddons;
    }

    return eligibleAddons;
  }

  mergeAddonData(addonConfig, productData) {
    // Find the first AVAILABLE variant (not just the first variant)
    const firstAvailableVariant = productData.variants.find(v => v.available) || productData.variants[0];
    const priceInDollars = firstAvailableVariant.price / 100;
    const hasMultipleVariants = productData.variants.length > 1;

    // If alwaysIncluded is true, price should always be 0 (free)
    const isAlwaysIncluded = addonConfig.alwaysIncluded || false;
    const finalPrice = isAlwaysIncluded ? 0 : (addonConfig.price ?? priceInDollars);

    return {
      id: productData.id,
      variantId: firstAvailableVariant.id,
      handle: productData.handle,
      name: addonConfig.name || productData.title,
      description: addonConfig.description || productData.description || '',
      price: finalPrice,
      compareAtPrice: firstAvailableVariant.compare_at_price ? firstAvailableVariant.compare_at_price / 100 : null,
      image: productData.featured_image || (productData.images && productData.images[0]),
      featured_image: firstAvailableVariant.featured_image?.src || productData.featured_image || (productData.images && productData.images[0]),
      url: `/products/${productData.handle}`,
      available: firstAvailableVariant.available,
      tier: addonConfig.tier || 'basic',
      tierValue: addonConfig.tierValue || 0,
      badge: addonConfig.badge || null,
      icon: addonConfig.icon || 'default',
      requiresConfiguration: addonConfig.requiresConfiguration || false,
      alwaysIncluded: isAlwaysIncluded,
      hasVariants: hasMultipleVariants,
      variants: productData.variants.map(v => ({
        id: v.id,
        title: v.title,
        name: v.name,
        price: v.price / 100,
        available: v.available,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        featured_image: v.featured_image?.src || null,
        sku: v.sku
      })),
      options: productData.options || [],
      productData: productData,
      config: addonConfig
    };
  }

  calculateTier(selectedAddons) {
    // DISCOUNT LOGIC (SPEND-BASED):
    // Spend £50+ in bundle → 15% off
    // Spend £150+ in bundle → 25% off
    // Spend £250+ in bundle → 30% off

    const totalPrice = selectedAddons.reduce((sum, addon) => sum + (addon.price || 0), 0);

    let currentTier = 'basic';
    let nextTier = null;
    let discount = '0%';
    let tierMethod = 'spend-based';

    // Pure spend-based tier calculation
    if (totalPrice >= 250) {
      // £250+ = 30% off
      currentTier = 'elite-bundle';
      nextTier = null;
      discount = '30%';
    } else if (totalPrice >= 150) {
      // £150-£249 = 25% off
      currentTier = 'complete-bundle';
      nextTier = 'elite-bundle';
      discount = '25%';
    } else if (totalPrice >= 50) {
      // £50-£149 = 15% off
      currentTier = 'value-bundle';
      nextTier = 'complete-bundle';
      discount = '15%';
    } else {
      // Under £50 = 0% off
      currentTier = 'basic';
      nextTier = 'value-bundle';
      discount = '0%';
    }

    const tierConfig = this.config.tiers[currentTier] || this.config.tiers.basic;
    const nextTierConfig = nextTier ? this.config.tiers[nextTier] : null;

    return {
      current: currentTier,
      next: nextTier,
      totalPrice,
      discount,
      config: tierConfig,
      nextTierConfig
    };
  }

  async addToCart(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const result = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { items: result.items }
      }));

      return result;
    } catch (error) {
      console.error('[BundleAdapter] Error adding to cart:', error);
      throw error;
    }
  }

  formatPrice(price) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(price * 100);
    }
    return `£${price.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  }
}

// Global instance
window.bundleAdapter = new BundleShopifyAdapter();
